import { applyPatch } from "fast-json-patch/index.mjs";
import { z } from "zod";

import { type RawOperation } from "../QueryEvaluator/TableMutation";
import { Tuples } from "../Tuples";
import { type JsonPathLiteral } from "../jsonTable/JsonPathLiteral";
import { jsonPathToMergePatchPath } from "../jsonTable/jsonPathToMergePatchPath";
import { parseJsonPath } from "../jsonTable/parseJsonPath";
import { type ComparisonOperator, isPrimitive, sqliteEquals } from "../types";
import { canonicalJsonString } from "../types/canonicalJsonString";

export const Id = Symbol("tupleId");
export const idOf = (tuple: Tuple<unknown> | unknown): string | undefined => {
	if (tuple instanceof Tuple) {
		return tuple.id;
	}
	if (
		typeof tuple === "object" &&
		tuple !== null &&
		Id in tuple &&
		typeof tuple[Id] === "string"
	) {
		return tuple[Id];
	}
	return undefined;
};

export class Tuple<T> {
	private readonly value: T;
	private hashCode: number | undefined;
	private readonly children: Map<keyof T | number, Tuple<unknown>> = new Map();
	private jsonString: string | undefined;

	private constructor(
		readonly id: string,
		value: T
	) {
		if (value instanceof BigInt || typeof value === "bigint") {
			throw new Error("BigInts are not supported");
		} else if (value instanceof Symbol || typeof value === "symbol") {
			throw new Error("Symbols are not supported");
		}

		if (isPrimitive(value)) {
			this.value = value;
		} else if (Array.isArray(value)) {
			const newValue = structuredClone(value);
			newValue[Id] = id;
			this.value = newValue;
		} else {
			// FIXME: We need to support both the object and non-object cases.
			this.value = { ...structuredClone(value), [Id]: id };
		}
	}

	static from<T>(id: string, value: T): Tuple<T> {
		return new Tuple(id, value);
	}

	contentId(): string {
		// FIXME: This fails on Unicode characters like ⊕
		return btoa(this.toJsonString());
	}

	applyPatch(patch: RawOperation[]): Tuple<T> {
		const updated = applyPatch(this.value, patch, true, false).newDocument;
		return Tuple.from(this.id, updated);
	}

	addOrReplace(path: JsonPathLiteral, value: unknown): Tuple<T> {
		const patchPath = jsonPathToMergePatchPath(path);
		let newValue: T;
		try {
			const addPatch: RawOperation = { op: "add", path: patchPath, value };
			newValue = applyPatch(this.value, [addPatch], true, false).newDocument;
		} catch (error) {
			const removePatch: RawOperation = { op: "replace", path: patchPath };
			newValue = applyPatch(this.value, [removePatch], true, false).newDocument;
		}
		return Tuple.from(this.id, newValue);
	}

	toJsonString(): string {
		if (this.jsonString === undefined) {
			this.jsonString = canonicalJsonString(this.value);
		}
		return this.jsonString;
	}

	equals(other: Tuple<T>): boolean {
		if (this.getHashCode() !== other.getHashCode()) {
			return false;
		}
		return this.compare("=", other);
	}

	derive<K extends keyof T, V extends T[K]>(key: K, value: V): Tuple<V> {
		return new Tuple<V>(`${this.id}.${String(key)}`, value);
	}

	get<K extends keyof T>(key: K): Tuple<T[K]> {
		if (!this.children.has(key)) {
			const child = this.value?.[key];
			if (child !== undefined) {
				this.children.set(key, this.derive(key, child) as Tuple<unknown>);
			}
		}
		return this.children.get(key) as Tuple<T[K]>;
	}

	getValue(): T {
		return this.value;
	}

	/**
	 * Efficient hash code calculation and caching
	 */
	getHashCode(): number {
		if (this.hashCode !== undefined) {
			return this.hashCode;
		}

		let hash = 0;
		if (this.value === null) {
			return 0;
		}

		if (typeof this.value === "object") {
			// For objects, hash the keys and values
			for (const [k, v] of Object.entries(this.value as object)) {
				hash = (hash << 5) - hash + this.hashString(k);
				hash = (hash << 5) - hash + this.derive(k as keyof T, v).getHashCode();
			}
		} else {
			// For primitives, hash the string representation
			hash = this.hashString(String(this.value));
		}

		this.hashCode = hash;
		return hash;
	}

	private hashString(str: string): number {
		let hash = 0;
		for (let i = 0; i < str.length; i++) {
			hash = (hash << 5) - hash + str.charCodeAt(i);
			hash = hash & hash; // Convert to 32-bit integer
		}
		return hash;
	}

	toJSON(): T {
		return this.value;
	}

	comparator<T>(b: Tuple<T>): number {
		if (this.compare("=", b)) {
			return 0;
		}
		return this.compare(">", b) ? 1 : -1;
	}

	compare<TCmp>(op: ComparisonOperator, other: Tuple<TCmp> | Tuples<TCmp> | null): boolean {
		if (other === null) {
			return false;
		}

		switch (op) {
			case "=":
				return this.sqliteEquals(other);
			case "!=":
			case "<>":
				return !this.sqliteEquals(other);
			case ">":
				return this.sqliteGreaterThan(other);
			case "<":
				return this.sqliteLessThan(other);
			case ">=":
				return this.sqliteGreaterThan(other) || this.sqliteEquals(other);
			case "<=":
				return this.sqliteLessThan(other) || this.sqliteEquals(other);
			case "LIKE":
				return this.sqliteLike(other);
			case "GLOB":
				return this.sqliteGlob(other);
			case "IN":
				return this.sqliteIn(other);
			case "NOT IN":
				return !this.sqliteIn(other);
			case "IS":
				return this.sqliteIs(other);
			case "IS NOT":
				return !this.sqliteIs(other);
		}
	}

	private sqliteEquals<TCmp>(_b: Tuple<TCmp> | Tuples<TCmp>): boolean {
		const tuple = _b instanceof Tuples ? _b.resolveToTuple() : _b;

		// Fast path: check hash codes
		if (tuple instanceof Tuple && this.getHashCode() !== tuple.getHashCode()) {
			return false;
		}

		const a = this.getValue();
		const b = tuple.getValue();

		if (a === null || b === null) {
			return false;
		}

		// FIXME.

		if (!isPrimitive(a) || !isPrimitive(b)) {
			return isDeepEqual(a, b);
		}

		return sqliteEquals(a, b);
	}

	private sqliteGreaterThan<TCmp>(_b: Tuple<TCmp> | Tuples<TCmp>): boolean {
		const tuple = _b instanceof Tuples ? _b.resolveToTuple() : _b;
		const a = this.getValue();
		const b = tuple.getValue();

		if (a === null || b === null) {
			return false;
		}
		if (typeof a !== typeof b) {
			return false; // No type coercion
		}
		if (typeof a === "number" && typeof b === "number") {
			return a > b;
		}
		if (typeof a === "string" && typeof b === "string") {
			return a.localeCompare(b) > 0;
		}
		return false;
	}

	private sqliteLessThan<TCmp>(_b: Tuple<TCmp> | Tuples<TCmp>): boolean {
		const tuple = _b instanceof Tuples ? _b.resolveToTuple() : _b;
		const a = this.getValue();
		const b = tuple.getValue();

		if (a === null || b === null) {
			return false;
		}
		if (typeof a !== typeof b) {
			return false; // No type coercion
		}
		if (typeof a === "number" && typeof b === "number") {
			return a < b;
		}
		if (typeof a === "string" && typeof b === "string") {
			return a.localeCompare(b) < 0;
		}
		return false;
	}

	private sqliteLike<TCmp>(_b: Tuple<TCmp> | Tuples<TCmp>): boolean {
		const tuple = _b instanceof Tuples ? _b.resolveToTuple() : _b;
		const a = this.getValue();
		const b = tuple.getValue();

		if (typeof a !== "string" || typeof b !== "string") {
			return false;
		}
		const pattern = b.replace(/%/g, ".*").replace(/_/g, ".");
		return new RegExp(`^${pattern}$`).test(a);
	}

	private sqliteGlob<TCmp>(_b: Tuple<TCmp> | Tuples<TCmp>): boolean {
		const tuple = _b instanceof Tuples ? _b.resolveToTuple() : _b;
		const a = this.getValue();
		const b = tuple.getValue();

		if (typeof a !== "string" || typeof b !== "string") {
			return false;
		}
		const pattern = b.replace(/\*/g, ".*").replace(/\?/g, ".");
		return new RegExp(`^${pattern}$`).test(a);
	}

	private sqliteIn<TCmp>(_b: Tuple<TCmp> | Tuples<TCmp>): boolean {
		if (_b instanceof Tuple) {
			return this.sqliteEquals(_b);
		}

		return _b.hasValue(this);
	}

	private sqliteIs<TCmp>(_b: Tuple<TCmp> | Tuples<TCmp>): boolean {
		const tuple = _b instanceof Tuples ? _b.resolveToTuple() : _b;
		const a = this.getValue();
		const b = tuple.getValue();

		// IS operator is the only one that can return true for NULL comparisons
		if (a === null && b === null) {
			return true;
		}
		return this.sqliteEquals(_b);
	}

	/**
	 * Gets the value at the specified JSON path
	 * @param path The JSON path to pluck from the tuple
	 * @returns The value at the path, wrapped in a new Tuple
	 */
	pluck<T>(path: JsonPathLiteral): Tuple<T> {
		const components = parseJsonPath(path);

		let current: unknown = this.value;
		let currentId = this.id;

		// Navigate through the path components
		for (const component of components) {
			if (current == null) {
				const componentPath =
					component.type === "object" ? `.${component.key}` : `[${component.index}]`;
				return Tuple.from(currentId + componentPath, null as T);
			}

			if ("index" in component) {
				if (!Array.isArray(current)) {
					return Tuple.from(currentId + `[${component.index}]`, null as T);
				}

				const actualIndex =
					typeof component.index === "object" && "fromEnd" in component.index
						? current.length - component.index.fromEnd
						: component.index;

				current = current[actualIndex] ?? null;
				currentId += `[${actualIndex}]`;
			} else {
				// Return null if trying to access property on array
				if (Array.isArray(current)) {
					return Tuple.from(currentId + `[${component.key}]`, null as T);
				}
				current = current[component.key] ?? null;
				currentId += `.${component.key}`;
			}
		}

		return Tuple.from(currentId, current as T);
	}
}

const isDeepEqual = function (x: unknown, y: unknown) {
	if (x === y) {
		return true;
	} else if (typeof x == "object" && x != null && typeof y == "object" && y != null) {
		if (Object.keys(x).length != Object.keys(y).length) {
			return false;
		}

		for (const prop in x) {
			if (Object.prototype.hasOwnProperty.call(y, prop)) {
				if (!isDeepEqual(x[prop], y[prop])) {
					return false;
				}
			} else {
				return false;
			}
		}

		return true;
	} else {
		return false;
	}
};

export const rawTupleSchema = z.object({
	id: z.string(),
	value: z.unknown().optional(),
});
export type RawTuple = z.infer<typeof rawTupleSchema>;

export const hydrateTuple = <T>(rawTuple: RawTuple): Tuple<T> =>
	Tuple.from<T>(rawTuple.id, rawTuple.value as T);

export const dehydrateTuple = <T>(tuple: Tuple<T>): RawTuple => ({
	id: tuple.id,
	value: tuple.getValue(),
});
